import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from '@emotion/styled';
import { toast } from 'react-toastify';

import Table from '../common/Table';
import Button from '../common/Button';
import Radios from '../common/Radios';
import { showModal } from '../../actions/modal';
import {
  fetchGetEmailServerSettings,
  fetchGetNotifications,
  fetchDeleteNotification,
} from '../../actions/settings';

import { parseError } from '../../helpers';

import { NOTIFICATION_TYPES, NOTIFICATION_CHANNELS } from '../../config';
import { useAutoTranslation } from '../../helpers/useAutoTranslation';

const data = [
  { label: `Системный почтовый ящик`, value: `system` },
  { label: `Свой почтовый ящик`, value: `own` },
];

const Wrapper = styled.div`
  padding: 0 !important;
  margin: 16px 0 0;

  > div {
    padding-left: 1.5rem;
    padding-right: 1.5rem;
  }

  .mail-sending {
    display: flex;
    align-items: center;
    padding-bottom: 16px;
  }

  .mail-sending-button {
    margin-left: 15px;
    i {
      color: red;
    }
  }

  .template-editing {
    margin: 16px 0;
    button {
      margin-top: 16px;
    }
  }

  .hidden {
    visibility: hidden;
  }
`;

const getTableData = (notifications, t) => {
  return {
    cols: [t('Событие'), t('Способ уведомления'), t('Получатели')],
    rows: notifications.map((r) => ({
      key: r.id,
      item: r,
      data: [
        { value: t(NOTIFICATION_TYPES[r.notificationType]) || r.notificationType, editItem: true },
        { value: r.notificationChannels.map((c) => NOTIFICATION_CHANNELS[c] || c).join(', ') },
        {
          value: r.notificationReceivers
            ?.replaceAll(',', ', ')
            .replace('KAK2C_CUSTOMER', t('Покупатели')),
        },
      ],
      isRemovable: true,
    })),
  };
};

const NotificationsBlock = (props) => {
  const { values } = props;
  const dispatch = useDispatch();
  const [radioValue, setRadioValue] = useState(``);
  const { emailSettings } = useSelector((state) => state.settings);

  const { t } = useAutoTranslation();

  const [loading, setLoading] = useState(true);
  const [notifications, setNotifications] = useState([]);

  const getNotifications = () => {
    return dispatch(fetchGetNotifications())
      .then((result) => {
        setNotifications(result || []);
      })
      .catch((err) => {
        toast.error(parseError(err));
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    (async function () {
      await Promise.allSettled([
        dispatch(fetchGetEmailServerSettings()),
        getNotifications(),
      ]).finally(() => {
        setLoading(false);
      });
    })();
  }, []);

  useEffect(() => {
    setRadioValue(emailSettings.isActivePersonalEmail ? `own` : `system`);
  }, [emailSettings]);

  const handleEditEmailTemplate = () => {
    dispatch(showModal('SettingEmailTemplate', { values }));
  };

  const handleEditPersonalEmailServer = () => {
    dispatch(showModal('EditPersonalEmailServer'));
  };

  const handleOpenNotificationModal = (initialData) => {
    dispatch(
      showModal('EditNotification', {
        initialData,
        notificationTypes: Object.keys(NOTIFICATION_TYPES).map((key) => ({
          label: NOTIFICATION_TYPES[key],
          value: key,
        })),
        submitCallback: async () => {
          setLoading(true);
          await getNotifications();
        },
      }),
    );
  };

  const handleRemoveNotification = async (data) => {
    setLoading(true);
    await dispatch(fetchDeleteNotification(data.id))
      .then(async () => {
        await getNotifications();
      })
      .catch((err) => {
        toast.error(parseError(err));
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <>
      <div className="border-bottom">
        <h4 className="header-title">{t('Оповещения')}</h4>
      </div>

      <Wrapper>
        <div className="block-subtitle">{t('Почтовый ящик')}</div>
        <div className="mail-sending">
          <Radios
            inlineLayout
            id="mailSending"
            name="mailSending"
            data={data}
            currentValue={radioValue}
            onChange={(value) => setRadioValue(value)}
          />
          <Button
            type="button"
            classType="btn-light"
            text={t('Настройки почты')}
            className={`mail-sending-button ${radioValue === `own` ? `` : `hidden`}`}
            icon={emailSettings.isActivePersonalEmail ? undefined : `fe-alert-circle`}
            onClick={handleEditPersonalEmailServer}
          />
        </div>
        <div className="border-bottom" />
        <div className="template-editing">
          <div className="block-subtitle">{t('Шаблон письма по заказам')}</div>
          <Button
            type="button"
            classType="btn-light"
            text={t('Настройки шаблона')}
            className="mr-3"
            onClick={handleEditEmailTemplate}
          />
        </div>
        <div className="border-bottom" />
        <Table
          id="notificationsTable"
          loading={loading}
          style={{ marginTop: '16px' }}
          smallTitle={t('Уведомления')}
          creatable
          removable
          createLabel={t('Новое событие')}
          data={getTableData(notifications, t)}
          handleNewItem={() => handleOpenNotificationModal(null)}
          handleEditItem={handleOpenNotificationModal}
          handleRemoveItem={handleRemoveNotification}
        />
      </Wrapper>
    </>
  );
};

export default NotificationsBlock;
